// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import { CartLineNotesStyles } from "@insite/content-library/Widgets/Cart/CartLineNotes";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import Plus from "@insite/mobius/Icons/Plus";
import { LinkPresentationProps } from "@insite/mobius/Link";
import { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { partNumbersStyle } from "@tps/App/ComponentExtensions/ProductPartNumbersExtensions";
import { getNotifyStyle } from "@tps/App/Components/Notify";
import { ProductPriceStyles } from "@tps/Overrides/Components/ProductPrice";
import { CartLineQuantityStyles } from "@tps/Overrides/Widgets/Cart/CartLineQuantity";
import IconTrashInactive from "@tps/XDL/Icons/IconTrashInactive";
import { fontConfig } from "@tps/XDL/PreStyle/typography";
import { color, createLinkIconStyles, ellipsis, xScreen } from "@tps/XDL/Utilities";
import { css } from "styled-components";

export interface CartLineSharedStyles {
    productImage?: ProductImageStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    addToListLink?: LinkPresentationProps;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    quantityTextField?: TextFieldPresentationProps;
    qtyLabel?: TypographyProps;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    costCodeGridItem?: GridItemProps;
    costCodeLabelText?: TypographyPresentationProps;
    costCodeValueText?: TypographyPresentationProps;
    priceHeadingAndText?: SmallHeadingAndTextStyles;
    promotionsContainer?: InjectableCss;
    promotionNameLabel?: TypographyPresentationProps;
    promotionNameText?: InjectableCss;
    removeCartLineIcon?: LinkPresentationProps;
    cartLineNotes?: CartLineNotesStyles;
    cartLineErrorMessageGridItem?: GridItemProps;
    errorMessage?: TypographyProps;
    productAvailability?: ProductAvailabilityStyles;
    configurationOptionText?: TypographyProps;
    configurationOptionLabel?: TypographyProps;
    unitOfMeasure?: TypographyProps;
    QtyRemoveGridItem?: InjectableCss;
    totalInnerWrapper?: InjectableCss;
    webDiscountMessage?: TypographyPresentationProps;
    distributeOrderButton?: ButtonPresentationProps;
    attributesWrapper?: InjectableCss;
    variantDisplay?: TypographyPresentationProps;
    approximateWeightDisplay?: TypographyPresentationProps;
    quantity?: CartLineQuantityStyles;
    discountValue?: TypographyPresentationProps;
}

const cartLineSharedStyles: CartLineSharedStyles = {
    productImage: {
        linkWrappingImage: {
            css: css`
                width: 80px;
                height: 80px;
                background-color: ${color("common.white")};
                justify-content: center;
                &&& > img {
                    max-width: 100%;
                    max-height: 100%;
                    height: auto;
                    object-fit: contain;
                }
            `,
        },
    },
    productDescription: {
        productDetailLink: {
            color: "text",
            typographyProps: {
                size: 14,
                weight: "bold",
                css: css`
                    line-height: 19px;
                    @media print {
                        display: block;
                        text-align: left;
                    }
                `,
            },
            css: css`
                line-height: 1;
                display: inline-block;
                align-self: flex-start;
                margin-bottom: 8px;
                height: auto;
                min-height: 38px;
                ${ellipsis(2)}
            `,
        },
        productDetailDescription: {
            size: 14,
            weight: "bold",
            css: css`
                letter-spacing: 0;
                line-height: 19px;
                height: auto;
                margin-bottom: 8px;
                min-height: 38px;
                ${ellipsis(2)}
            `,
        },
    },
    productPartNumbers: {
        container: {
            css: css`
                margin-bottom: 10px;
                ${xScreen("lte", "md")} {
                    > div {
                        gap: 0 22px;
                    }
                }
            `,
        },
        erpNumberGridItem: {
            css: css`
                flex-basis: auto;
                word-break: keep-all;
            `,
        },
    },
    addToListLink: {
        ...createLinkIconStyles(Plus, {
            css: css`
                white-space: nowrap;
                ${IconWrapper} {
                    width: 14px;
                    height: 14px;
                }
            `,
        }),
    },
    productPrice: {
        priceLabelText: {
            size: 12,
            weight: "normal",
            css: css`
                line-height: 17px;
                text-transform: capitalize;
                padding: 0;
                margin-bottom: 4px;
                display: none;
                ${xScreen("lte", "md")} {
                    display: block;
                }
                @media print {
                    &&& {
                        font-size: 14px;
                        line-height: 16px;
                    }
                }
            `,
        },
        priceWrapper: {
            css: css`
                line-height: 19px;
            `,
        },
        price: {
            priceText: {
                size: 14,
                weight: "bold",
                lineHeight: "19px",
                css: css`
                    display: inline-block;
                `,
            },
            unitOfMeasure: {
                unitOfMeasureText: {
                    size: 12,
                    lineHeight: "17px",
                    weight: "normal",
                    css: css`
                        white-space: nowrap;
                    `,
                },
            },
            discountWrapper: {
                css: css`
                    display: flex;
                    gap: 8px;
                `,
            },
            originalPriceLabel: {
                ...fontConfig.xSmall,
                weight: fontConfig.weight.regular,
                css: css`
                    text-decoration: line-through;
                    font-style: italic;
                    align-self: flex-end;
                `,
            },
        },
        wrapper: {
            css: css`
                ${xScreen("lte", "md")} {
                    margin-bottom: 12px;
                }
            `,
        },
    },
    extendedUnitNetPriceText: {
        extendedStyles: {
            heading: {
                size: 12,
                lineHeight: "17px",
                weight: "normal",
                css: css`
                    line-height: 17px;
                    text-transform: capitalize;
                    padding: 0;
                    margin-bottom: 4px;
                    display: none;
                    ${xScreen("lte", "md")} {
                        display: block;
                    }
                    @media print {
                        &&& {
                            font-size: 14px;
                            line-height: 16px;
                        }
                    }
                `,
            },
            text: {
                size: 14,
                weight: "bold",
                lineHeight: "16px",
                css: css``,
            },
        },
    },
    costCodeGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    costCodeLabelText: {
        ...partNumbersStyle.label,
    },
    costCodeValueText: {
        ...partNumbersStyle.value,
    },
    configurationOptionText: {
        ...partNumbersStyle.value,
    },
    configurationOptionLabel: {
        ...partNumbersStyle.label,
    },
    promotionsContainer: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-bottom: 10px;
        `,
    },
    promotionNameLabel: {
        ...partNumbersStyle.label,
    },
    promotionNameText: {
        ...partNumbersStyle.value,
    },
    qtyLabel: {
        as: "label",
        size: 12,
        weight: "normal",
        lineHeight: "17px",
        css: css`
            margin-bottom: 4px;
            display: none;
            ${xScreen("lte", "md")} {
                display: block;
            }
            @media print {
                &&& {
                    font-size: 14px;
                    line-height: 16px;
                }
            }
        `,
    },
    removeCartLineIcon: {
        icon: {
            iconProps: {
                src: IconTrashInactive,
                size: 12,
                color: "common.ui4",
                css: css`
                    margin-right: 0;
                `,
            },
        },
        css: css`
            margin-left: 20px;
            &&&& {
                outline-style: none;
            }
        `,
    },
    cartLineNotes: {
        wrapper: {
            css: css`
                ${xScreen("lte", "md")} {
                    width: 100%;
                }
            `,
        },
    },
    cartLineErrorMessageGridItem: {
        css: css`
            display: flex;
            flex-direction: column;
            ${xScreen("lte", "md")} {
                margin-top: 10px;
            }
        `,
    },
    errorMessage: {
        ...getNotifyStyle("danger"),
    },
    productAvailability: {
        messageGridItem: {
            css: css`
                & > div {
                    margin-bottom: 10px;
                }
            `,
        },
    },
    unitOfMeasure: {
        size: 12,
        weight: "normal",
        css: css`
            line-height: 13px;
            text-transform: capitalize;
        `,
    },
    QtyRemoveGridItem: {
        css: css`
            display: flex;
        `,
    },
    totalInnerWrapper: {
        css: css`
            width: 100%;
            display: flex;
            flex-direction: column;
            align-items: flex-end;
            ${xScreen("lte", "md")} {
                align-items: flex-start;
            }
        `,
    },
    webDiscountMessage: {
        size: 12,
        lineHeight: "17px",
        weight: "bold",
        css: css`
            margin-top: 4px;
            display: flex;
            column-gap: 12px;
            flex-wrap: wrap;
        `,
    },
    discountValue: {
        css: css`
            ${xScreen("lte", "xs")} {
                width: 100%;
            }
        `,
    },
    distributeOrderButton: {
        sizeVariant: "small",
        css: css`
            margin-top: 8px;
            padding-left: 6px;
            padding-right: 6px;
        `,
    },
    attributesWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            margin-top: auto;
        `,
    },
    variantDisplay: {
        size: 12,
        lineHeight: "17px",
        css: css`
            margin-bottom: 4px;
            margin-top: 6px;
            ${xScreen("lte", "md")} {
                margin-top: 0;
            }
        `,
        transform: "capitalize",
    },
    approximateWeightDisplay: {
        size: 12,
        lineHeight: "17px",
    },
    quantity: {
        qtyFieldStyles: {
            wrapper: {
                css: css`
                    max-width: 107px;
                    min-width: 107px;
                `,
            },
        },
    },
};

export default cartLineSharedStyles;
